import { useEffect, useRef, type ReactNode } from 'react';
import { BRAND_WIZARD_STEP_COUNT, type BrandWizardStep } from '@mg/shared';
import { Card, CardBody } from '../../components/ui/Card';
import { stepOfText } from './wizardCopy';

/**
 * One step's card (spec:96; web.md §4.1): "Step N of 8", the step's title and one-line description, then
 * its fields. When the step changes (not on the first one shown), the page scrolls back to the top and
 * the title takes the focus, so a screen reader hears where it is (spec:157).
 */
export function StepCard({
  step,
  titleId,
  title,
  description,
  children,
}: {
  step: BrandWizardStep;
  titleId: string;
  title: string;
  description: string;
  children: ReactNode;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  const shown = useRef<BrandWizardStep | null>(null);

  useEffect(() => {
    // The first step shown (StrictMode runs this twice with it) keeps the focus where it is.
    const changed = shown.current !== null && shown.current !== step;
    shown.current = step;
    if (!changed) return;
    const el = heading.current;
    if (!el) return;
    const scroller = el.closest<HTMLElement>('[data-page-scroller]');
    if (scroller) scroller.scrollTop = 0;
    el.focus({ preventScroll: true });
  }, [step]);

  return (
    <Card>
      <section aria-labelledby={titleId}>
        <div className="border-b border-line-soft px-5 py-4">
          <p className="text-xs font-medium text-ink-muted">{stepOfText(step, BRAND_WIZARD_STEP_COUNT)}</p>
          <h2 ref={heading} id={titleId} tabIndex={-1} className="mt-0.5 text-lg font-semibold text-ink">
            {title}
          </h2>
          <p className="mt-0.5 text-sm text-ink-muted">{description}</p>
        </div>
        <CardBody className="space-y-6 py-5">{children}</CardBody>
      </section>
    </Card>
  );
}
